import { AppRoute } from '@core/route-data/models/app-routes';
import { canDeactivateComponentGuard } from '@pages/third-party-idp/guards/can-deactivate-component.guard';
import { TranslationFeature } from '@core/translation/models/translation-feature.enum';
import { provideStates } from '@ngxs/store';
import { thirdPartyIdpState } from '@pages/third-party-idp/store';
import { securityPageState } from '@pages/security/store';
import { hasPermissionGuard } from '@core/permissions/guards/has-permission.guard';
import { accessGroupsState } from '@pages/access-groups/store';
import { userManagementState } from '@pages/user-management/store/store';
import { setupSummaryState } from '@pages/setup-summary/store';
import { moduleManagementState } from '@pages/organization-module-management/store';
import { PageTitleTranslationKey } from '@core/page-title/models/page-title-translation-key.enum';
import { hasIpAccessUnsavedChangesGuard } from '@pages/security/guards/has-ip-access-unsaved-changes.guard';
import { provideFeatureTranslations } from '@core/translation/models/feature-translations.provider';
import { isFeatureEnabledGuard } from '@core/features/guards/is-feature-enabled.guard';
import { Feature } from '@kaseya-shared';
import { roleManagementStates } from '@pages/role-management/store';
import { adminSettingsViewPermissions } from '@core/permissions/constants/permissions.const';
import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import { redirectToFn } from '@core/navigation/helpers/redirect-to';

export const adminSettingsRoutes: AppRoute[] = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: redirectToFn(TopNavigationPath.AdminSettings, 'setup-summary'),
    data: { prefetchTarget: 'setup-summary' },
  },
  {
    path: 'third-party-idp',
    canMatch: [hasPermissionGuard(adminSettingsViewPermissions.thirdPartyIdp)],
    canDeactivate: [canDeactivateComponentGuard],
    loadComponent: () =>
      import('@pages/third-party-idp/components/third-party-idp-page/third-party-idp-page.component').then(
        modules => modules.ThirdPartyIdpPageComponent
      ),
    data: {
      disableMobileVersion: true,
      title: PageTitleTranslationKey.ThirdPartySso,
    },
    providers: [provideFeatureTranslations(TranslationFeature.ThirdPartyIdp), provideStates([...thirdPartyIdpState])],
  },
  {
    path: 'security',
    canMatch: [hasPermissionGuard(adminSettingsViewPermissions.security)],
    loadComponent: () =>
      import('@pages/security/components/security-page/security-page.component').then(({ SecurityPageComponent }) => SecurityPageComponent),
    data: {
      disableMobileVersion: true,
      title: PageTitleTranslationKey.Security,
    },
    canDeactivate: [hasIpAccessUnsavedChangesGuard],
    providers: [provideFeatureTranslations(TranslationFeature.Security), provideStates([...securityPageState])],
  },
  {
    path: 'module-management',
    canMatch: [hasPermissionGuard(adminSettingsViewPermissions.moduleManagement)],
    loadComponent: () =>
      import('@pages/organization-module-management/components/module-management-page/module-management-page.component').then(
        ({ ModuleManagementPageComponent }) => ModuleManagementPageComponent
      ),
    data: {
      disableMobileVersion: true,
      title: PageTitleTranslationKey.ModuleManagement,
    },
    providers: [provideStates([...moduleManagementState]), provideFeatureTranslations(TranslationFeature.OrganizationModuleManagement)],
  },
  {
    path: 'access-groups',
    canMatch: [hasPermissionGuard(adminSettingsViewPermissions.accessGroups)],
    loadComponent: () =>
      import('@pages/access-groups/components/access-groups-page/access-groups-page.component').then(
        modules => modules.AccessGroupsPageComponent
      ),
    providers: [provideStates(accessGroupsState), provideFeatureTranslations(TranslationFeature.AccessGroups)],
    data: {
      disableMobileVersion: true,
      title: PageTitleTranslationKey.Groups,
    },
  },
  {
    path: 'user-management',
    canMatch: [hasPermissionGuard(adminSettingsViewPermissions.userManagement)],
    loadComponent: () =>
      import('@pages/user-management/components/user-management/user-management.component').then(
        modules => modules.UserManagementComponent
      ),
    data: {
      disableMobileVersion: true,
      title: PageTitleTranslationKey.UserManagement,
      fitsScreenWidth: true,
    },
    providers: [provideStates([...userManagementState])],
  },
  {
    path: 'setup-summary',
    canMatch: [hasPermissionGuard(adminSettingsViewPermissions.setupSummary)],
    loadComponent: () =>
      import('@pages/setup-summary/components/setup-summary-page/setup-summary-page.component').then(
        modules => modules.SetupSummaryPageComponent
      ),
    data: {
      disableMobileVersion: true,
      title: PageTitleTranslationKey.SetupSummary,
    },
    providers: [provideStates([...setupSummaryState]), provideFeatureTranslations(TranslationFeature.SetupSummary)],
  },
  {
    path: 'role-management',
    canMatch: [isFeatureEnabledGuard(Feature.CustomRoles), hasPermissionGuard(adminSettingsViewPermissions.roleManagement)],
    loadComponent: () =>
      import('@pages/role-management/components/role-management-page/role-management-page.component').then(
        ({ RoleManagementPageComponent }) => RoleManagementPageComponent
      ),
    data: {
      title: PageTitleTranslationKey.RoleManagement,
      fitsScreenWidth: true,
    },
    providers: [provideFeatureTranslations(TranslationFeature.RoleManagement), provideStates(roleManagementStates)],
  },
];
